import ContextButton from "@via/components/context-button/context-button.tsx";
import { SparklesIcon } from "@heroicons/react/24/outline";
import { Tooltip } from "react-tooltip";
import { useIntegrationTokens } from "@via/api/integration-tokens.api";
import { TableContextMenuInterface } from "@via/utils/interfaces";
import { useCortexContext } from "@via/context/cortex-context";
import { useAuth } from "@via/hooks/useAuth";

interface TokenOption extends TableContextMenuInterface {
  tokenId: number;
}

interface CortexButtonProps {
  onAfterOpen?: () => void;
}

const CLAUDE_CODE_OPTION: TableContextMenuInterface = {
  id: "cortex-claude-code",
  name: "Claude Code",
  href: "#",
};

const CortexButton = ({ onAfterOpen }: CortexButtonProps) => {
  const { setIsDrawerOpen, setSelectedTokenId, setDrawerMode } =
    useCortexContext();
  const { user } = useAuth();
  const canUseInsight = !!user?.featureFlags?.FF_AI_REPORT_INTERPRET;
  const canUseClaudeCode = !!user?.featureFlags?.FF_CLAUDE_CHANNEL;

  // Fetch AI service tokens to get serviceTokenId
  const { data: aiTokenData, isLoading: isTokenDataLoading } =
    useIntegrationTokens(
      0,
      10,
      [],
      [{ id: "tokenTypeIn", value: ["openai", "gemini", "anthropic"] }],
      true
    );

  // Transform tokens to ContextButton options
  const tokenOptions: TokenOption[] = canUseInsight
    ? (aiTokenData?.data?.map(token => ({
        name: token.name,
        href: "#", // Required by interface but not used
        tokenId: token.id,
      })) ?? [])
    : [];
  // Claude Code mode talks to the person's own session and needs no AI token (agent-6-2).
  const options: TableContextMenuInterface[] = canUseClaudeCode
    ? [...tokenOptions, CLAUDE_CODE_OPTION]
    : tokenOptions;

  const handleTokenSelect = (option: TableContextMenuInterface) => {
    if (option.id === CLAUDE_CODE_OPTION.id) {
      setDrawerMode("claude");
    } else {
      setSelectedTokenId((option as TokenOption).tokenId);
      setDrawerMode("insight");
    }
    setIsDrawerOpen(true);
    onAfterOpen?.();
  };

  return (
    <div className="relative">
      <ContextButton
        id="cortex-button"
        text={
          <span className="flex items-center space-x-2">
            <SparklesIcon className="w-4 h-4" />
            <span>AI Insight</span>
          </span>
        }
        options={options}
        onSelect={handleTokenSelect}
        loading={isTokenDataLoading && !canUseClaudeCode}
        disabled={options.length === 0 && !isTokenDataLoading}
        className="bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-all duration-200 shadow-sm hover:shadow-md"
      />
      {options.length === 0 && !isTokenDataLoading && (
        <Tooltip
          anchorSelect="#cortex-button"
          content="No AI integration tokens available. Please create one in your profile page."
        />
      )}
    </div>
  );
};

export default CortexButton;
